/**
 * Google Analytics 4 (GA4) integration
 *
 * Implements the pattern from Mykola Aleksandrov:
 * https://www.mykolaaleksandrov.dev/posts/2025/11/react-google-analytics-implementation/
 *
 * CRITICAL: The gtag function must push the native JavaScript `arguments` object
 * to dataLayer rather than a rest/spread array (...args), ensuring GA4 processes
 * events correctly without silent beacon failures.
 */

export const GA_MEASUREMENT_ID =
  import.meta.env.VITE_GA_MEASUREMENT_ID || 'G-VFVXRW3XFK'

let isInitialized = false

export function initGA() {
  if (typeof window === 'undefined' || isInitialized) {
    return
  }
  isInitialized = true

  window.dataLayer = window.dataLayer || []

  if (typeof window.gtag !== 'function') {
    window.gtag = function () {
      // eslint-disable-next-line prefer-rest-params
      window.dataLayer.push(arguments)
    }
  }

  const existingScript = document.querySelector(
    `script[src*="googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}"]`
  )
  if (!existingScript) {
    const script = document.createElement('script')
    script.async = true
    script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`
    document.head.appendChild(script)

    window.gtag('js', new Date())
    window.gtag('config', GA_MEASUREMENT_ID, {
      send_page_view: false,
      transport_type: 'beacon',
    })
  }
}

/**
 * Track page view in React Single Page Application (SPA)
 */
export function trackPageView(path, title) {
  if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
    window.gtag('event', 'page_view', {
      page_path: path,
      page_title: title || (typeof document !== 'undefined' ? document.title : ''),
    })
  }
}

/**
 * Track custom event in Google Analytics
 */
export function trackEvent(eventName, eventParams = {}) {
  if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
    window.gtag('event', eventName, eventParams)
  }
}
